import posthog from 'posthog-js'

export type ErrorProperties = Record<string, unknown>

type PendingException = {
  error: unknown
  properties?: ErrorProperties
}

/**
 * Errors reported before `posthog.init` ran. The client is initialised from an
 * effect in the app providers, so anything thrown during the first render would
 * otherwise be dropped. Bounded so a render loop cannot grow it without limit.
 */
const MAX_PENDING = 25
const pending: PendingException[] = []

/**
 * PostHog is a browser-only client and is only usable once `posthog.init` ran
 * inside the app providers. Server components, route handlers and the redux
 * store all share these helpers, so every entry point has to tolerate both a
 * missing `window` and an uninitialised client.
 */
const getClient = (): typeof posthog | undefined => {
  if (typeof window === 'undefined') {
    return undefined
  }

  return posthog.__loaded ? posthog : undefined
}

/**
 * Reports an error to PostHog error tracking.
 *
 * On the server there is no client to report to, so the error is logged. In the
 * browser it is buffered until `flushPendingExceptions` runs after init.
 */
export const captureException = (
  error: unknown,
  properties?: ErrorProperties
): void => {
  const client = getClient()

  if (client) {
    client.captureException(error, properties)
    return
  }

  console.error('[error-tracking] captureException', error, properties ?? {})

  if (typeof window !== 'undefined' && pending.length < MAX_PENDING) {
    pending.push({ error, properties })
  }
}

/**
 * Reports a message without an accompanying `Error` instance. PostHog error
 * tracking groups on exceptions, so the message is wrapped to keep it in the
 * same stream as the rest of the errors.
 */
export const captureMessage = (
  message: string,
  properties?: ErrorProperties
): void => {
  captureException(new Error(message), properties)
}

/**
 * Registers properties that are attached to every subsequent event, including
 * captured exceptions. Replaces the per-scope tags used previously.
 */
export const setErrorContext = (properties: ErrorProperties): void => {
  getClient()?.register(properties)
}

/**
 * Hands PostHog everything that was captured before it finished initialising.
 * Call once, directly after `posthog.init`.
 */
export const flushPendingExceptions = (): void => {
  const client = getClient()

  if (!client) {
    return
  }

  for (const { error, properties } of pending.splice(0)) {
    client.captureException(error, properties)
  }
}
